import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { TimerStatus } from '@/types';
import { recordPomodoro } from '@/hooks/usePomodoroHistory';

const STORAGE_KEY = 'pomodoro-settings';
const TIMER_STATE_KEY = 'pomodoro-timer-state';
const CYCLES_KEY = 'pomodoro-cycles';

function getStoredSettings() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        focusTime: parsed.focusTime ?? 25,
        breakTime: parsed.breakTime ?? 5,
        longBreakTime: parsed.longBreakTime ?? 15,
        cyclesUntilLongBreak: parsed.cyclesUntilLongBreak ?? 4,
      };
    }
  } catch {}
  return { focusTime: 25, breakTime: 5, longBreakTime: 15, cyclesUntilLongBreak: 4 };
}

function getStoredCycles(): number {
  try {
    const stored = localStorage.getItem(CYCLES_KEY);
    if (stored) return Math.max(0, parseInt(stored, 10) || 0);
  } catch {}
  return 0;
}

function getStoredTimerState() {
  try {
    const stored = localStorage.getItem(TIMER_STATE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);

      if (parsed.status === 'running' || parsed.status === 'break') {
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((parsed.endTime - now) / 1000));

        if (remaining > 0) {
          return { ...parsed, timeLeft: remaining };
        }

        if (parsed.status === 'running') {
          const duration = parsed.cycleStartTime
            ? Math.round((parsed.endTime - parsed.cycleStartTime) / 60000)
            : parsed.focusTime ?? 25;
          return {
            ...parsed,
            status: 'finished' as TimerStatus,
            timeLeft: 0,
            lastDuration: duration,
          };
        }
        return null;
      }

      if (parsed.status === 'finished' || parsed.status === 'paused') {
        return parsed;
      }
    }
  } catch {}
  return null;
}

interface TimerContextType {
  focusTime: number;
  breakTime: number;
  longBreakTime: number;
  cyclesUntilLongBreak: number;
  completedCycles: number;
  timeLeft: number;
  status: TimerStatus;
  lastDuration: number | null;
  isLongBreak: boolean;
  nextBreakIsLong: boolean;
  setFocusTime: (time: number) => void;
  setBreakTime: (time: number) => void;
  setLongBreakTime: (time: number) => void;
  setCyclesUntilLongBreak: (n: number) => void;
  start: () => void;
  pause: () => void;
  resume: () => void;
  reset: () => void;
  startBreak: () => void;
  startLongBreak: () => void;
  clearLastDuration: () => void;
  isActive: boolean;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export function TimerProvider({ children }: { children: ReactNode }) {
  const storedSettings = getStoredSettings();
  const storedState = getStoredTimerState();

  const [focusTime, setFocusTime] = useState(storedSettings.focusTime);
  const [breakTime, setBreakTime] = useState(storedSettings.breakTime);
  const [longBreakTime, setLongBreakTime] = useState(storedSettings.longBreakTime);
  const [cyclesUntilLongBreak, setCyclesUntilLongBreak] = useState(storedSettings.cyclesUntilLongBreak);
  const [completedCycles, setCompletedCycles] = useState(getStoredCycles());
  const [timeLeft, setTimeLeft] = useState(storedState?.timeLeft ?? storedSettings.focusTime * 60);
  const [status, setStatus] = useState<TimerStatus>(storedState?.status ?? 'idle');
  const [cycleStartTime, setCycleStartTime] = useState<number | null>(storedState?.cycleStartTime ?? null);
  const [lastDuration, setLastDuration] = useState<number | null>(storedState?.lastDuration ?? null);
  const [isLongBreak, setIsLongBreak] = useState<boolean>(storedState?.isLongBreak ?? false);

  const endTimeRef = useRef<number | null>(
    storedState && (storedState.status === 'running' || storedState.status === 'break')
      ? Date.now() + (storedState.timeLeft ?? 0) * 1000
      : null
  );

  // Persist settings
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ focusTime, breakTime, longBreakTime, cyclesUntilLongBreak }));
  }, [focusTime, breakTime, longBreakTime, cyclesUntilLongBreak]);

  // Persist cycles
  useEffect(() => {
    localStorage.setItem(CYCLES_KEY, String(completedCycles));
  }, [completedCycles]);

  const persistState = useCallback(() => {
    if (status === 'running' || status === 'break') {
      localStorage.setItem(TIMER_STATE_KEY, JSON.stringify({
        status,
        endTime: endTimeRef.current,
        cycleStartTime,
        timeLeft,
        focusTime,
        isLongBreak,
      }));
    } else if (status === 'paused') {
      localStorage.setItem(TIMER_STATE_KEY, JSON.stringify({
        status: 'paused',
        timeLeft,
        cycleStartTime,
        focusTime,
      }));
    } else if (status === 'finished') {
      localStorage.setItem(TIMER_STATE_KEY, JSON.stringify({
        status: 'finished',
        timeLeft: 0,
        lastDuration,
        focusTime,
      }));
    } else if (status === 'idle') {
      endTimeRef.current = null;
      localStorage.removeItem(TIMER_STATE_KEY);
    }
  }, [status, timeLeft, cycleStartTime, lastDuration, focusTime, isLongBreak]);

  const prevStatusRef = useRef(status);
  useEffect(() => {
    if (prevStatusRef.current !== status) {
      prevStatusRef.current = status;
      persistState();
      return;
    }
    if (status === 'paused' || status === 'finished') {
      persistState();
    }
  }, [status, persistState]);

  useEffect(() => {
    if (status === 'idle') {
      setTimeLeft(focusTime * 60);
    }
  }, [focusTime, status]);

  // Visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      try {
        const stored = localStorage.getItem(TIMER_STATE_KEY);
        if (!stored) return;
        const parsed = JSON.parse(stored);

        if ((parsed.status === 'running' || parsed.status === 'break') && parsed.endTime) {
          const remaining = Math.max(0, Math.floor((parsed.endTime - Date.now()) / 1000));

          if (remaining <= 0) {
            if (parsed.status === 'running') {
              const duration = parsed.cycleStartTime
                ? Math.round((parsed.endTime - parsed.cycleStartTime) / 60000)
                : parsed.focusTime ?? focusTime;
              setLastDuration(duration);
              setCompletedCycles((c) => c + 1);
              recordPomodoro(duration);
              setStatus('finished');
              setTimeLeft(0);
            } else {
              if (parsed.isLongBreak) {
                setCompletedCycles(0);
              }
              setIsLongBreak(false);
              setStatus('idle');
              setTimeLeft(focusTime * 60);
            }
          } else {
            setTimeLeft(remaining);
          }
        }
      } catch {}
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [focusTime]);

  // Tick
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    if (status === 'running' || status === 'break') {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            if (status === 'running') {
              const duration = cycleStartTime
                ? Math.round((Date.now() - cycleStartTime) / 60000)
                : focusTime;
              setLastDuration(duration);
              setCompletedCycles((c) => c + 1);
              recordPomodoro(duration);
              setStatus('finished');
              return 0;
            } else {
              if (isLongBreak) {
                setCompletedCycles(0);
              }
              setIsLongBreak(false);
              setStatus('idle');
              return focusTime * 60;
            }
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [status, focusTime, cycleStartTime, isLongBreak]);

  const start = useCallback(() => {
    const now = Date.now();
    setCycleStartTime(now);
    endTimeRef.current = now + focusTime * 60 * 1000;
    setTimeLeft(focusTime * 60);
    setStatus('running');
  }, [focusTime]);

  const pause = useCallback(() => setStatus('paused'), []);

  const resume = useCallback(() => {
    endTimeRef.current = Date.now() + timeLeft * 1000;
    setStatus('running');
  }, [timeLeft]);

  const reset = useCallback(() => {
    setStatus('idle');
    setTimeLeft(focusTime * 60);
    setCycleStartTime(null);
    setLastDuration(null);
    setCompletedCycles(0);
    setIsLongBreak(false);
    localStorage.removeItem(TIMER_STATE_KEY);
  }, [focusTime]);

  const startBreak = useCallback(() => {
    const breakSeconds = breakTime * 60;
    endTimeRef.current = Date.now() + breakSeconds * 1000;
    setTimeLeft(breakSeconds);
    setIsLongBreak(false);
    setStatus('break');
  }, [breakTime]);

  const startLongBreak = useCallback(() => {
    const breakSeconds = longBreakTime * 60;
    endTimeRef.current = Date.now() + breakSeconds * 1000;
    setTimeLeft(breakSeconds);
    setIsLongBreak(true);
    setStatus('break');
  }, [longBreakTime]);

  const clearLastDuration = useCallback(() => setLastDuration(null), []);

  const isActive = status !== 'idle';
  const nextBreakIsLong = cyclesUntilLongBreak > 0 && completedCycles > 0 && completedCycles % cyclesUntilLongBreak === 0;

  return (
    <TimerContext.Provider value={{
      focusTime,
      breakTime,
      longBreakTime,
      cyclesUntilLongBreak,
      completedCycles,
      timeLeft,
      status,
      lastDuration,
      isLongBreak,
      nextBreakIsLong,
      setFocusTime,
      setBreakTime,
      setLongBreakTime,
      setCyclesUntilLongBreak,
      start,
      pause,
      resume,
      reset,
      startBreak,
      startLongBreak,
      clearLastDuration,
      isActive,
    }}>
      {children}
    </TimerContext.Provider>
  );
}

export function useTimer() {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
}
